import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHONE = "+38978351715";
const PHONE_DISPLAY = "+389 78 351 715";
const WHATSAPP_URL = "https://wa.me/38978351715";
const EMAIL = "sktrade.importexport@gmail.com";

export default function Contact() {
  const { t } = useT();
  const tf = t.contact.form;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = tf.errors.name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = tf.errors.email;
    if (form.message.trim().length < 5) next.message = tf.errors.message;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.status === "received") {
        toast.success(tf.successToast);
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        toast.error(tf.errors.submit);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || tf.genericError;
      toast.error(typeof msg === "string" ? msg : tf.errors.submit);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/15 focus:border-white/60 focus:outline-none px-4 py-3 text-white placeholder-white/30 text-sm transition-colors rounded-none";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0A] border-b border-white/10"
    >
      <div className="grid grid-cols-12">
        <div className="col-span-12 md:col-span-5 border-r border-white/10 px-6 sm:px-10 py-10 md:py-20 flex flex-col gap-12">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">
              {t.contact.label}
            </p>
            <h2
              data-testid="contact-headline"
              className="font-display font-bold uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            >
              {t.contact.headlineL1}
              <br />
              <span className="text-white/40">{t.contact.headlineL2}</span>
            </h2>
            {t.contact.intro && (
              <p className="mt-6 text-base text-white/70 max-w-md">
                {t.contact.intro}
              </p>
            )}
          </div>

          <div className="grid gap-0 border-t border-white/10">
            <a
              href={`tel:${PHONE}`}
              data-testid="contact-phone"
              className="group flex items-center justify-between border-b border-white/10 py-5 hover:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-center gap-4">
                <Phone size={16} strokeWidth={1.5} className="text-white/60" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.contact.callLabel}
                  </p>
                  <p className="font-display text-lg text-white mt-1">
                    {PHONE_DISPLAY}
                  </p>
                </div>
              </div>
              <ArrowUpRight
                size={18}
                strokeWidth={1.25}
                className="text-white/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
              />
            </a>

            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email"
              className="group flex items-center justify-between border-b border-white/10 py-5 hover:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-center gap-4">
                <Mail size={16} strokeWidth={1.5} className="text-white/60" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.contact.emailLabel}
                  </p>
                  <p className="font-display text-base sm:text-lg text-white mt-1 break-all">
                    {EMAIL}
                  </p>
                </div>
              </div>
              <ArrowUpRight
                size={18}
                strokeWidth={1.25}
                className="text-white/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
              />
            </a>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="group flex items-center justify-between border-b border-white/10 py-5 hover:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-center gap-4">
                <MessageCircle
                  size={16}
                  strokeWidth={1.5}
                  className="text-white/60"
                />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.contact.whatsappLabel}
                  </p>
                  <p className="font-display text-lg text-white mt-1">
                    {PHONE_DISPLAY}
                  </p>
                </div>
              </div>
              <ArrowUpRight
                size={18}
                strokeWidth={1.25}
                className="text-white/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
              />
            </a>

            <div className="flex items-center gap-4 py-5">
              <MapPin size={16} strokeWidth={1.5} className="text-white/60" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                  {t.contact.officeLabel}
                </p>
                <p className="font-display text-lg text-white mt-1">
                  {t.contact.officeValue}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7 px-6 sm:px-10 py-10 md:py-20">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="grid grid-cols-12 gap-4"
            noValidate
          >
            <div className="col-span-12 md:col-span-6">
              <label className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                {tf.nameLabel}
              </label>
              <input
                data-testid="contact-input-name"
                type="text"
                value={form.name}
                onChange={update("name")}
                placeholder={tf.namePh}
                className={`${inputCls} mt-2`}
                autoComplete="name"
              />
              {errors.name && (
                <p className="text-xs text-red-400 mt-1" data-testid="error-name">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="col-span-12 md:col-span-6">
              <label className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                {tf.emailLabel}
              </label>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder={tf.emailPh}
                className={`${inputCls} mt-2`}
                autoComplete="email"
              />
              {errors.email && (
                <p
                  className="text-xs text-red-400 mt-1"
                  data-testid="error-email"
                >
                  {errors.email}
                </p>
              )}
            </div>

            <div className="col-span-12">
              <label className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                {tf.phoneLabel}
              </label>
              <input
                data-testid="contact-input-phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder={tf.phonePh}
                className={`${inputCls} mt-2`}
                autoComplete="tel"
              />
            </div>

            <div className="col-span-12">
              <label className="text-[10px] uppercase tracking-[0.3em] text-white/50">
                {tf.messageLabel}
              </label>
              <textarea
                data-testid="contact-input-message"
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder={tf.messagePh}
                className={`${inputCls} mt-2 resize-none`}
              />
              {errors.message && (
                <p
                  className="text-xs text-red-400 mt-1"
                  data-testid="error-message"
                >
                  {errors.message}
                </p>
              )}
            </div>

            <div className="col-span-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
              {tf.eta ? (
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/40">
                  {tf.eta}
                </p>
              ) : (
                <span />
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="inline-flex items-center justify-center gap-3 bg-white text-black px-6 py-3.5 text-xs uppercase tracking-[0.25em] font-medium hover:bg-neutral-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {tf.sending}
                  </>
                ) : (
                  <>
                    {tf.submit}
                    <ArrowUpRight size={14} strokeWidth={1.75} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
